import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { Send, Plus, Trash2, MessageSquare, Clock, ChevronRight, Bot, User, BookOpen, Sparkles, CheckCircle2, ArrowRight } from '../components/CustomIcons';

const API = process.env.REACT_APP_BACKEND_URL;
const TAVUS_DEPLOYMENT_ID = '701a88ca-5198-4358-b955-6681e791ed1b';

function TavusAvatar({ token, userName }) {
  const [status, setStatus] = useState(() => (window.customElements?.get('tavus-embed') ? 'ready' : 'loading'));
  const [ctx, setCtx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/courses/tutor-context`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setCtx(d); })
      .catch(() => { if (!cancelled) setCtx({ has_context: false }); });
    return () => { cancelled = true; };
  }, [token]);

  const firstName = (ctx?.learner_name || userName || '').split(' ')[0];
  const conversationalContext = ctx?.has_context
    ? [
        'You are a friendly, encouraging vocational education counselor on the AssistivOS tablet platform inside a correctional facility.',
        firstName && `The learner's name is ${firstName}.`,
        `They are enrolled in the "${ctx.pathway}" pathway and are ${ctx.progress}% through it.`,
        ctx.course && `They are currently working on the course "${ctx.course}"${ctx.lesson ? `, lesson "${ctx.lesson}"` : ''}.`,
        'Start by referencing what they are working on. Explain concepts simply, quiz them gently, and keep them motivated toward their credential and employment after release.',
      ].filter(Boolean).join(' ')
    : 'You are a friendly, encouraging vocational education counselor on the AssistivOS platform. Help the learner with their coursework, explain concepts simply, and keep them motivated.';
  const greeting = ctx?.has_context && ctx.course
    ? `Hey${firstName ? ` ${firstName}` : ''}! Ready to keep going on ${ctx.course}?`
    : undefined;

  useEffect(() => {
    if (window.customElements?.get('tavus-embed')) { setStatus('ready'); return undefined; }
    const onLoad = () => setStatus('ready');
    const onError = () => setStatus('failed');
    let script = document.querySelector('script[data-tavus-embed]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://unpkg.com/@tavus/embed@latest';
      script.async = true;
      script.dataset.tavusEmbed = '1';
      document.head.appendChild(script);
    }
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    return () => { script.removeEventListener('load', onLoad); script.removeEventListener('error', onError); };
  }, []);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px', overflow: 'hidden' }} data-testid="live-avatar-panel">
      <div style={{ marginBottom: '14px' }}>
        <h2 style={{ margin: '0 0 4px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '18px', color: 'var(--text-primary)' }}>Live AI Counselor</h2>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-dim)' }}>
          Talk face-to-face with your counselor{ctx?.has_context && ctx.course ? <> — it already knows you're on <b style={{ color: 'var(--text-primary)' }}>{ctx.course}</b>{ctx.lesson ? <> · {ctx.lesson}</> : null}</> : ''}. Allow camera &amp; microphone access when your browser asks.
        </p>
      </div>
      <div style={{ flex: 1, minHeight: 0, borderRadius: '16px', overflow: 'hidden', border: '1px solid var(--border-default)', background: '#060E1C', position: 'relative' }}>
        {status === 'loading' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', color: 'var(--text-dim)', fontSize: '13px' }} data-testid="live-avatar-loading">
            <Bot size={28} color="#F97316" />
            Connecting to your live counselor…
          </div>
        )}
        {status === 'failed' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', color: 'var(--text-dim)', fontSize: '13px', padding: '24px', textAlign: 'center' }} data-testid="live-avatar-error">
            <Bot size={28} color="#F97316" />
            The live counselor couldn't load. Check your connection and refresh the page.
          </div>
        )}
        {status === 'ready' && ctx !== null && (
          <tavus-embed
            deployment-id={TAVUS_DEPLOYMENT_ID}
            conversational-context={conversationalContext}
            {...(greeting ? { 'custom-greeting': greeting } : {})}
            style={{ display: 'block', width: '100%', height: '100%' }}
            data-testid="tavus-embed"
          />
        )}
      </div>
    </div>
  );
}

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = (Date.now() - new Date(isoStr)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function RecapCard({ recap, onNewConversation, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(4,10,20,0.72)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      data-testid="recap-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: '100%', maxWidth: '440px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderTop: '3px solid var(--orange)', borderRadius: '18px', padding: '26px 26px 22px', boxShadow: '0 30px 80px rgba(0,0,0,.5)' }}
        role="dialog" aria-modal="true" aria-label="Session recap" data-testid="recap-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
          <Sparkles size={15} color="#F97316" aria-hidden="true" />
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '10px', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--orange)', fontWeight: 700 }}>Session Recap</span>
          <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-dim)' }}>{recap.user_message_count} question{recap.user_message_count === 1 ? '' : 's'} · {recap.message_count} messages</span>
        </div>
        <h3 style={{ margin: '0 0 8px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '19px', color: 'var(--text-primary)', letterSpacing: '-0.01em' }} data-testid="recap-title">{recap.title}</h3>
        <p style={{ margin: '0 0 16px', fontSize: '13.5px', lineHeight: 1.65, color: 'var(--text-secondary)' }} data-testid="recap-summary">{recap.summary}</p>
        {recap.topics?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }} data-testid="recap-topics">
            {recap.topics.map((t) => (
              <span key={t} style={{ fontSize: '11px', fontWeight: 600, color: 'var(--orange)', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '99px', padding: '4px 10px' }}>{t}</span>
            ))}
          </div>
        )}
        {recap.win && (
          <div style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', marginBottom: '10px' }} data-testid="recap-win">
            <CheckCircle2 size={15} color="#4ade80" style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
            <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>{recap.win}</p>
          </div>
        )}
        {recap.next_step && (
          <div style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', padding: '12px 14px', background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.16)', borderRadius: '12px', marginBottom: '18px' }} data-testid="recap-next-step">
            <ArrowRight size={15} color="#F97316" style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
            <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: 'var(--text-primary)' }}><b style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Next step:</b> {recap.next_step}</p>
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onNewConversation}
            style={{ flex: 1, padding: '11px', background: 'var(--orange)', border: 'none', borderRadius: '10px', cursor: 'pointer', color: '#111', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px' }}
            data-testid="recap-new-conversation-btn">
            Start a New Conversation
          </button>
          <button onClick={onClose}
            style={{ padding: '11px 16px', background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: '10px', cursor: 'pointer', color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px' }}
            data-testid="recap-close-btn">
            Keep Chatting
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AiTutor() {
  const { token, user } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState('chat');
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [recap, setRecap] = useState(null);
  const [recapLoading, setRecapLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Parse URL context params (e.g. from CoursePlayer "Open in AI Tutor" button)
  const searchParams = new URLSearchParams(location.search);
  const ctxPathway = searchParams.get('pathway') || '';
  const ctxCourse = searchParams.get('course') || '';
  const ctxLesson = searchParams.get('lesson') || '';
  const hasContext = !!(ctxPathway || ctxLesson);
  const contextString = [
    ctxPathway ? `Pathway: ${ctxPathway}` : '',
    ctxCourse ? `Course: ${ctxCourse}` : '',
    ctxLesson ? `Lesson: ${ctxLesson}` : '',
  ].filter(Boolean).join(' | ');

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API}/api/ai-tutor/sessions`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      const data = await res.json();
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) { console.error('sessions-load error:', e); }
  };

  useEffect(() => {
    fetchSessions();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSession = async (sessionId) => {
    setActiveSessionId(sessionId);
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API}/api/ai-tutor/sessions/${sessionId}/history`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      const data = await res.json();
      setMessages(Array.isArray(data) ? data.map(m => ({ role: m.role, content: m.content, id: m.id })) : []);
    } catch (e) {
      console.error('history-load error:', e);
      setMessages([]);
    }
    setLoadingHistory(false);
    inputRef.current?.focus();
  };

  const startNewSession = () => {
    const newId = `session-${Date.now()}`;
    setActiveSessionId(newId);
    const welcomeText = hasContext
      ? `Hi! I'm Assistiv AI, your personal learning counselor. I can see you're working on **${ctxLesson || ctxCourse || ctxPathway}**. Ask me anything about this lesson, the concepts involved, or your upcoming exam. What would you like help with?`
      : "Hi! I'm Assistiv AI, your personal learning counselor. I'm here to help you understand course material, prep for certification exams, or talk through career questions. What are you working on today?";
    setMessages([{
      role: 'assistant',
      content: welcomeText,
      id: 'welcome'
    }]);
    inputRef.current?.focus();
  };

  const sendMessage = async () => {
    if (!input.trim() || streaming) return;
    const sessionId = activeSessionId || `session-${Date.now()}`;
    if (!activeSessionId) setActiveSessionId(sessionId);

    const userMsg = { role: 'user', content: input.trim(), id: `user-${Date.now()}` };
    setMessages(prev => [...prev, userMsg]);
    const aiMsg = { role: 'assistant', content: '', id: `ai-${Date.now()}`, streaming: true };
    setMessages(prev => [...prev, aiMsg]);
    setInput('');
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/api/ai-tutor/chat`, {
        method: 'POST',
        headers: token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: userMsg.content, session_id: sessionId, context: contextString }),
      });

      const reader = resp.body?.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      while (reader) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const text = line.slice(6);
            if (text === '[DONE]') break;
            accumulated += text;
            setMessages(prev => prev.map(m =>
              m.id === aiMsg.id ? { ...m, content: accumulated } : m
            ));
          }
        }
      }

      setMessages(prev => prev.map(m =>
        m.id === aiMsg.id ? { ...m, streaming: false } : m
      ));
      await fetchSessions();
    } catch (e) {
      console.error('send-message error:', e);
      setMessages(prev => prev.map(m =>
        m.id === aiMsg.id ? { ...m, content: 'Sorry, I had trouble connecting. Please try again.', streaming: false } : m
      ));
    }
    setStreaming(false);
  };

  const hasUserMessage = messages.some((m) => m.role === 'user' && m.content);

  const fetchRecap = async () => {
    if (!activeSessionId || recapLoading) return;
    setRecapLoading(true);
    try {
      const res = await fetch(`${API}/api/ai-tutor/sessions/${activeSessionId}/recap`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (res.ok) setRecap(await res.json());
    } catch (e) { console.error('recap error:', e); }
    setRecapLoading(false);
  };

  const deleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await fetch(`${API}/api/ai-tutor/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setMessages([]);
      }
    } catch (e) { console.error('delete-session error:', e); }
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 0px)', background: 'var(--bg-page)', overflow: 'hidden' }} data-testid="ai-tutor-page">

      {/* ── Sidebar ── */}
      <div style={{ width: '260px', borderRight: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', flexShrink: 0, background: 'var(--bg-surface)' }}>
        {/* Header */}
        <div style={{ padding: '18px 16px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'var(--orange)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bot size={15} color="#111" />
            </div>
            <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>Assistiv AI</span>
          </div>
          <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', borderRadius: '10px', padding: '3px', marginBottom: '12px' }} role="tablist" aria-label="Counselor mode">
            {[{ k: 'chat', l: 'Text Chat' }, { k: 'avatar', l: 'Live Avatar' }].map((t) => (
              <button
                key={t.k}
                role="tab"
                aria-selected={mode === t.k}
                onClick={() => setMode(t.k)}
                data-testid={`tutor-mode-${t.k}`}
                style={{
                  flex: 1, padding: '7px 4px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                  fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '12px',
                  background: mode === t.k ? 'var(--orange)' : 'transparent',
                  color: mode === t.k ? '#111' : 'var(--text-dim)',
                  transition: 'background .15s, color .15s',
                }}
              >{t.l}</button>
            ))}
          </div>
          <button onClick={startNewSession}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '9px', background: 'var(--orange)', border: 'none', borderRadius: '10px', cursor: 'pointer', color: '#111', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px' }}
            aria-label="Start new conversation"
            data-testid="new-conversation-btn">
            <Plus size={14} aria-hidden="true" />
            New Conversation
          </button>
        </div>

        {/* Sessions list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {sessions.length === 0 ? (
            <div style={{ padding: '20px 12px', textAlign: 'center' }}>
              <MessageSquare size={28} style={{ color: 'var(--text-dim)', marginBottom: '8px' }} />
              <p style={{ fontSize: '12px', color: 'var(--text-dim)', margin: 0 }}>No conversations yet</p>
            </div>
          ) : sessions.map(s => (
            <div key={s.session_id}
              onClick={() => loadSession(s.session_id)}
              style={{ padding: '10px 12px', borderRadius: '9px', cursor: 'pointer', marginBottom: '3px', display: 'flex', gap: '8px', alignItems: 'flex-start', justifyContent: 'space-between', background: activeSessionId === s.session_id ? 'rgba(249,115,22,0.1)' : 'transparent', border: activeSessionId === s.session_id ? '1px solid rgba(249,115,22,0.2)' : '1px solid transparent', transition: 'all 0.15s' }}
              data-testid={`session-${s.session_id}`}
              onMouseEnter={e => { if (activeSessionId !== s.session_id) e.currentTarget.style.background = 'var(--bg-elevated)'; }}
              onMouseLeave={e => { if (activeSessionId !== s.session_id) e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: '0 0 3px', fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.preview || 'Empty session'}
                </p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Clock size={10} style={{ color: 'var(--text-dim)' }} />
                  <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{timeAgo(s.last_at)}</span>
                  <span style={{ fontSize: '10px', color: 'var(--text-dim)' }}>· {s.message_count} msgs</span>
                </div>
              </div>
              <button onClick={e => deleteSession(e, s.session_id)}
                style={{ padding: '3px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', borderRadius: '4px', flexShrink: 0, opacity: 0.6 }}
                aria-label={`Delete conversation: ${s.preview || 'Empty session'}`}
                data-testid={`delete-session-${s.session_id}`}
                onMouseEnter={e => { e.currentTarget.style.color = '#E05555'; e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-dim)'; e.currentTarget.style.opacity = '0.6'; }}>
                <Trash2 size={12} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main area ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {mode === 'avatar' ? (
          <TavusAvatar token={token} userName={user?.profile?.name || ''} />
        ) : !activeSessionId ? (
          /* Welcome screen */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '18px', background: 'var(--orange)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
              <Bot size={32} color="#111" />
            </div>
            <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '24px', color: 'var(--text-primary)', margin: '0 0 10px', textAlign: 'center' }}>
              Assistiv AI Counselor
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px', textAlign: 'center', maxWidth: '480px', lineHeight: 1.6, margin: '0 0 20px' }}>
              Your personal learning guide — ask anything about your courses, certifications, or career path.
            </p>
            {hasContext && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '10px', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', marginBottom: '20px', maxWidth: '520px' }}>
                <BookOpen size={15} color="#F97316" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '13px', color: 'var(--orange)', fontFamily: 'Inter, sans-serif' }}>
                  Context loaded: <strong>{ctxLesson || ctxCourse || ctxPathway}</strong>
                </span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', width: '100%', maxWidth: '600px' }}>
              {[
                "Explain Ohm's Law in simple terms",
                "How do I prepare for the CDL permit test?",
                "What certifications can I earn in HVAC?",
                "Help me understand the GED Math section",
              ].map(prompt => (
                <button key={prompt} onClick={() => { startNewSession(); setTimeout(() => { setInput(prompt); }, 100); }}
                  style={{ padding: '14px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '12px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'left', fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', transition: 'all 0.18s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--orange)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
                  {prompt}
                  <ChevronRight size={14} style={{ flexShrink: 0 }} />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Session bar */}
            <div style={{ padding: '9px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>Conversation in progress</span>
              <button onClick={fetchRecap} disabled={!hasUserMessage || streaming || recapLoading}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 13px', borderRadius: '9px', cursor: !hasUserMessage || streaming || recapLoading ? 'not-allowed' : 'pointer', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.22)', color: 'var(--orange)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '12px', opacity: !hasUserMessage || streaming ? 0.45 : 1, transition: 'opacity .15s' }}
                aria-label="Wrap up this session and see a recap"
                data-testid="recap-btn">
                <Sparkles size={13} aria-hidden="true" />
                {recapLoading ? 'Writing recap…' : 'Wrap Up & Recap'}
              </button>
            </div>
            {/* Context banner */}
            {hasContext && (
              <div style={{ padding: '8px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(249,115,22,0.06)' }}>
                <BookOpen size={13} color="#F97316" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: 'var(--orange)', fontFamily: 'Inter, sans-serif' }}>
                  {contextString}
                </span>
              </div>
            )}
            {/* Messages */}
            <div role="log" aria-label="Conversation messages" aria-live="polite" style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {loadingHistory ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }} aria-live="polite">Loading conversation...</div>
              ) : messages.map((msg, idx) => (
                <div key={msg.id || idx} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}
                  data-testid={`message-${msg.role}`}
                  aria-label={`${msg.role === 'assistant' ? 'Assistiv AI' : 'You'}: ${msg.content?.slice(0, 60)}`}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: msg.role === 'assistant' ? 'var(--orange)' : 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-hidden="true">
                    {msg.role === 'assistant' ? <Bot size={16} color="#111" /> : <User size={16} color="var(--text-secondary)" />}
                  </div>
                  <div style={{ maxWidth: '72%', padding: '14px 16px', borderRadius: msg.role === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px', background: msg.role === 'user' ? 'var(--orange)' : 'var(--bg-surface)', border: msg.role === 'user' ? 'none' : '1px solid var(--border-subtle)' }}>
                    <p style={{ margin: 0, fontSize: '14px', color: msg.role === 'user' ? '#111' : 'var(--text-primary)', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: 'Inter, sans-serif' }}>
                      {msg.content}
                      {msg.streaming && <span aria-hidden="true" style={{ display: 'inline-block', width: '8px', height: '14px', background: 'var(--orange)', marginLeft: '4px', animation: 'pulse 1s ease-in-out infinite', borderRadius: '2px', verticalAlign: 'text-bottom' }} />}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <label htmlFor="ai-tutor-textarea" className="sr-only">Message Assistiv AI</label>
              <textarea
                id="ai-tutor-textarea"
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Ask me anything about your courses, exams, or career path... (Enter to send)"
                rows={2}
                aria-label="Message Assistiv AI"
                aria-describedby="ai-tutor-hint"
                style={{ flex: 1, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '12px', padding: '12px 14px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', fontFamily: 'Inter, sans-serif', resize: 'none', lineHeight: 1.5, transition: 'border-color 0.2s' }}
                onFocus={e => { e.target.style.borderColor = 'var(--orange)'; }}
                onBlur={e => { e.target.style.borderColor = 'var(--border-subtle)'; }}
                data-testid="ai-tutor-input"
              />
              <span id="ai-tutor-hint" className="sr-only">Press Enter to send, Shift+Enter for new line</span>
              <button onClick={sendMessage} disabled={streaming || !input.trim()}
                style={{ padding: '12px 18px', background: streaming || !input.trim() ? '#2A2A2A' : 'var(--orange)', border: 'none', borderRadius: '12px', cursor: streaming || !input.trim() ? 'not-allowed' : 'pointer', color: streaming || !input.trim() ? '#555' : '#111', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', flexShrink: 0, transition: 'all 0.2s' }}
                aria-label={streaming ? 'Assistiv AI is thinking' : 'Send message'}
                aria-disabled={streaming || !input.trim()}
                data-testid="send-message-btn">
                <Send size={16} aria-hidden="true" />
                {streaming ? 'Thinking...' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
      {recap && (
        <RecapCard
          recap={recap}
          onClose={() => setRecap(null)}
          onNewConversation={() => { setRecap(null); startNewSession(); }}
        />
      )}
    </div>
  );
}
